import BackupTableOutlined from "@mui/icons-material/BackupTableOutlined";
import FlashOff from "@mui/icons-material/FlashOff";
import PollOutlined from "@mui/icons-material/PollOutlined";
import RepeatOutlined from "@mui/icons-material/RepeatOutlined";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useSupabaseCallback } from "../../../../server/supabase/hooks";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { uuidv4 } from "../../../../utils/uuid";
import { isEmailChannel } from "../../../channels/types";
import { prettyChannelName } from "../../../channels/utils";
import { EmailMomentLoader } from "../../../email-moment/server/email-moment-loader";
import { ConditionalTooltip } from "../../../generic/components/conditional-tooltip";
import { convertLexicalDesign } from "../../../moment/component/channel/utils/convert-lexical-design";

import type { UUID } from "../../../../utils/uuid";
import type { ChannelName } from "../../../channels/types";
import type { EmailMoment } from "../../../email-moment/types";
import type { SerialisedLexicalEditorDesign } from "../../../message/editor/types/serialised-editor-design";
import type { FC } from "react";

export const FollowUpMessageImport: FC<{
  id: UUID | null;
  channel: ChannelName;
  setDesign: (design: SerialisedLexicalEditorDesign) => void;
  useLexicalEditor?: boolean;
  originalMomentUseLexicalEditor?: boolean;
  onEmailMomentImport?: (emailMoment: EmailMoment) => void;
  onDynamicSegmentIdMapImport?: (map: Record<string, string> | null) => void;
}> = ({
  id,
  channel,
  setDesign,
  useLexicalEditor = true,
  originalMomentUseLexicalEditor = true,
  onEmailMomentImport,
  onDynamicSegmentIdMapImport,
}) => {
  const dialog = useDialog();
  const [loading, setLoading] = useState(false);

  const { handleClose } = dialog;

  const handleImport = useSupabaseCallback(
    async ({ supabase }) => {
      if (!id || !channel) return;

      setLoading(true);

      if (!useLexicalEditor && onEmailMomentImport) {
        // Newsletter import: load the original moment's email_moment and dynamic_segment_id_map
        const { data: momentData, error: momentError } = await supabase
          .from("moment")
          .select("email_moment_id, dynamic_segment_id_map")
          .eq("id", id)
          .single();

        if (momentError || !momentData?.email_moment_id) {
          console.error("Error importing newsletter message:", momentError);
          toast.error("Unable to import message");
          setLoading(false);
          handleClose();
          return;
        }

        const { data: loadedEmailMoment } = await new EmailMomentLoader(supabase).load(
          momentData.email_moment_id,
        );

        if (!loadedEmailMoment) {
          toast.error("Unable to import message");
          setLoading(false);
          handleClose();
          return;
        }

        // Assign a new id so the follow-up creates its own email_moment record
        // instead of overwriting the original moment's email_moment.
        onEmailMomentImport({
          ...loadedEmailMoment,
          id: uuidv4(),
        });

        // Import the dynamic_segment_id_map from the original moment
        if (onDynamicSegmentIdMapImport) {
          onDynamicSegmentIdMapImport(momentData.dynamic_segment_id_map ?? null);
        }

        setLoading(false);
        toast.success("Content imported successfully");
        handleClose();
        return;
      }

      // Lexical import
      const { data, error } = await supabase
        .from("moment")
        .select("design,channel")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error importing message:", error);
        toast.error("Unable to import message");
        setLoading(false);
        return;
      }

      if (!data.channel) {
        console.error("Parent message has no channel selected");
        toast.error("Parent message has no channel selected");
        setLoading(false);
        handleClose();
        return;
      }

      if (!data.design) {
        console.error("Parent message has no design");
        toast.error("Parent message has no design");
        setLoading(false);
        handleClose();
        return;
      }

      setDesign(
        convertLexicalDesign(
          channel,
          data.channel,
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          data.design as SerialisedLexicalEditorDesign,
          false,
          [],
          ["dynamic", "dynamic-toolbar", "poll"],
        ),
      );

      setLoading(false);
      toast.success("Content imported successfully");
      handleClose();
    },
    [
      id,
      channel,
      handleClose,
      setDesign,
      useLexicalEditor,
      onEmailMomentImport,
      onDynamicSegmentIdMapImport,
    ],
  );

  const changedOffNewsletterEditor = !originalMomentUseLexicalEditor && !isEmailChannel(channel);

  return (
    <>
      <ConditionalTooltip
        arrow
        situations={[
          {
            title: `Newsletter content cannot be imported for ${prettyChannelName(channel)}`,
            condition: changedOffNewsletterEditor,
          },
        ]}
      >
        <span>
          <Button
            variant="outlined"
            color="primary"
            onClick={dialog.handleOpen}
            data-analytics-id="follow-up-message-import"
            disabled={!id || !channel || changedOffNewsletterEditor}
            loading={loading}
          >
            Import content from original Communication
          </Button>
        </span>
      </ConditionalTooltip>
      <Dialog
        open={dialog.open}
        onClose={dialog.handleClose}
        slotProps={{
          paper: {
            sx: {
              width: 700,
              maxWidth: "100%",
            },
          },
        }}
      >
        <DialogTitle sx={{ pt: 3 }}>
          <Typography variant="body2" fontSize="14px" fontWeight={700}>
            Import content from original Communication?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Stack gap={2} fontSize="14px" fontWeight={400}>
            <Typography variant="body2">This will:</Typography>
            <Stack gap={1.5} pl={2}>
              <Stack direction="row" alignItems="center" gap={1}>
                <RepeatOutlined sx={{ fontSize: "16px" }} />
                <Typography variant="body2">Replace any content you’ve already added</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <FlashOff sx={{ fontSize: "16px" }} />
                <Typography variant="body2">Remove any dynamic content</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <PollOutlined sx={{ fontSize: "16px" }} />
                <Typography variant="body2">
                  Link any polls and their results to this Follow-Up instead of the original
                  Communication
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <BackupTableOutlined sx={{ fontSize: "16px" }} />
                <Typography variant="body2">
                  Update formatting if you’re using a different channel from the original
                  Communication
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="body2">
              Bear in mind, if the content of the original Communication changes, it won’t be
              updated in this Follow-Up.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button
            onClick={dialog.handleClose}
            color="primary"
            variant="text"
            data-analytics-id="follow-up-message-import-cancel"
            loading={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleImport()}
            color="primary"
            variant="contained"
            data-analytics-id="follow-up-message-import-import"
            loading={loading}
          >
            Import content
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
